import type { ResolvedPlace } from '../types/groups';

/**
 * Keeping a tile's map pin honest about which venue it points at.
 *
 * The failure this exists to prevent, from a live trip: the Sunday lunch tile
 * read "Bairro Alto Hotel" and pinned Costa da Caparica. The host had opened an
 * existing tile, retyped the location, searched for the new venue, been told
 * "Nothing found" — the lookup was broken for every query — and saved anyway.
 * The tile's text changed. The coordinates did not. A guest navigating to that
 * pin drives to the wrong side of a river.
 *
 * So a place is bound to the location text it was captured against. Change the
 * text without rebinding and the pin is no longer evidence of anything: it is
 * cleared on save, unless the host says in as many words to keep it. A wrong pin
 * is worse than no pin — no pin makes a guest ask; a wrong pin makes them drive.
 */

/** Where the currently-held place came from. */
export type PlaceBindingOrigin =
  /** Read off the saved item. Nobody has confirmed in this session that it is
   *  still the right venue for the text beside it. */
  | 'stored'
  /** The host either picked it from a lookup just now, or was shown the pin and
   *  chose to keep it. Either way they had eyes on the venue. */
  | 'confirmed';

export interface PlaceBinding {
  place: ResolvedPlace | null;
  origin: PlaceBindingOrigin;
  /** The location text this place was bound against. */
  boundLocationName: string;
}

/** The fields an itinerary or room-block save sends for the place. */
export interface PlaceSaveFields {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  tripsbyLocationId: string | undefined;
  googlePlaceId: string | undefined;
  address: string | undefined;
  businessPhone: string | undefined;
  websiteUrl: string | undefined;
  mapsUrl: string | undefined;
}

const normalize = (value: string | null | undefined): string => (value ?? '').trim();

export const EMPTY_BINDING: PlaceBinding = {
  place: null,
  origin: 'confirmed',
  boundLocationName: '',
};

/** The place a saved item already carries, held as unconfirmed until the host
 *  either rebinds it or says to keep it. */
export function storedBinding(
  place: ResolvedPlace | null,
  locationName: string | null | undefined,
): PlaceBinding {
  return {
    place,
    origin: place ? 'stored' : 'confirmed',
    boundLocationName: normalize(locationName),
  };
}

/** A place the host just picked, bound to whatever the location field says at
 *  that moment. Renaming the tile afterwards is fine — "Check in" is a perfectly
 *  good label for a hotel they chose thirty seconds ago and can still see. */
export function pickedBinding(
  place: ResolvedPlace | null,
  locationName: string | null | undefined,
): PlaceBinding {
  return {
    place,
    origin: 'confirmed',
    boundLocationName: normalize(locationName),
  };
}

/** The host, shown that the pin predates their edit, says keep it anyway. That
 *  is a decision rather than an accident, which is the whole distinction. */
export function keepBinding(
  binding: PlaceBinding,
  locationName: string | null | undefined,
): PlaceBinding {
  return {
    place: binding.place,
    origin: 'confirmed',
    boundLocationName: normalize(locationName),
  };
}

/**
 * Does this pin still describe the text next to it?
 *
 * Only unconfirmed (stored) bindings can go stale. Text similarity is not the
 * test and never was: a host is free to call Hotel do Chiado "Check in". What
 * matters is whether anyone has looked at this venue during this edit.
 */
export function isBindingStale(
  binding: PlaceBinding,
  locationName: string | null | undefined,
): boolean {
  if (!binding.place) return false;
  if (binding.origin !== 'stored') return false;
  return normalize(locationName) !== binding.boundLocationName;
}

/** Does the pin actually point anywhere? Used to word the warning: losing an
 *  address is a smaller thing to say than losing a map pin. */
export function bindingHasPin(binding: PlaceBinding): boolean {
  return binding.place?.latitude != null && binding.place?.longitude != null;
}

/**
 * The place fields to send with a save.
 *
 * Three cases, and the middle one is the fix:
 *
 * - No place at all → everything undefined. The API COALESCEs the snapshot
 *   columns, so an omitted snapshot leaves a stored one alone; that is what lets
 *   a client which predates the picker save without blanking an address.
 * - Stale binding → cleared, explicitly. Coordinates go as null (assigned
 *   directly by the update, and a NOT NULL/NULL pair violates the coordinate
 *   check constraint, so both move together). The snapshot strings go as empty
 *   rather than null precisely because the API COALESCEs them — a null there
 *   would preserve the very address and maps link we are trying to drop.
 * - A confirmed place → sent as picked.
 */
export function placeFieldsForSave(
  binding: PlaceBinding,
  locationName: string | null | undefined,
): PlaceSaveFields {
  if (isBindingStale(binding, locationName)) {
    return {
      latitude: null,
      longitude: null,
      tripsbyLocationId: '',
      googlePlaceId: '',
      address: '',
      businessPhone: '',
      websiteUrl: '',
      mapsUrl: '',
    };
  }

  const place = binding.place;
  return {
    latitude: place?.latitude ?? undefined,
    longitude: place?.longitude ?? undefined,
    tripsbyLocationId: place?.tripsbyLocationId ?? undefined,
    googlePlaceId: place?.googlePlaceId ?? undefined,
    address: place?.address ?? undefined,
    businessPhone: place?.businessPhone ?? undefined,
    websiteUrl: place?.websiteUrl ?? undefined,
    mapsUrl: place?.mapsUrl ?? undefined,
  };
}
